import { useEffect } from 'react';
import { isEqual } from 'lodash-es';
import { z } from 'zod';
import { gql } from '@soundxyz/gql-string';
import { getManyFromList } from '@soundxyz/utils';
import { enumChecker } from '@soundxyz/utils/constants/enums';
import { usePostsEnabled } from '@components/Feed/Post/usePostsEnabled';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import {
  ActivityFeedGroupFilterOption,
  ActivityFeedHookDocument,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { PersistenceStorage } from '@store/persistence';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY } from '@utils/constants';
import type { ActivityFeedStateType } from './store';
import {
  addDividerDateCheck,
  FilteredActivityFeedTypes,
  filterOutNonExistentType,
  filterOutRevertedActivity,
  PAGE_SIZE,
  ActivityFeedState as store,
  useActivityFeedState,
} from './store';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ActivityFeedHook(
    $pagination: CursorConnectionArgs
    $activityFeedFilter: ActivityFeedFilterArgs
    $activityFeedGroupFilter: ActivityFeedGroupFilterArgs
    $userId: UUID
  ) {
    activityFeed(filter: $activityFeedFilter) {
      id
      activityFeedGroups: groups(pagination: $pagination, filter: $activityFeedGroupFilter) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            latestActivityOccurenceAt
            rankingScore
            information {
              __typename
              ... on UserCollectedManySongsAggregate {
                ...UserCollectedManySongsActivity
              }

              ... on UserCreatedPostAggregate {
                ...FeedActivityTypePost
              }

              ... on SongCollectedByManyAggregate {
                ...ReleaseCollectedByManyActivity
              }
              ... on ReleaseDroppedAggregate {
                release {
                  id
                  ...ArtistDropRelease
                }
              }
              ... on ShelfCreatedAggregate {
                shelf {
                  id
                }
                ...PlaylistCreated
              }

              ... on ReleasesAddedToShelfAggregate {
                shelf {
                  id
                }
                releasesAdded {
                  id
                }
                ...ReleasesAddedToPlaylist
              }

              ... on UserLikedSongsAggregate {
                shelf {
                  id
                }
                user {
                  id
                }
                releasesLiked {
                  id
                }
                ...ReleasesLiked
              }
              ... on UserLikedPlaylistAggregate {
                shelf {
                  id
                }
                user {
                  id
                }
                ...LikedPlaylist
              }
            }
          }
        }
      }
    }
  }
`);

export const isActivityFeedType = enumChecker(FilteredActivityFeedTypes);

const ActivityFeedMainFilterPersistence = PersistenceStorage({
  key: '@soundxyz/activityFeedFilter',
  schema: z.nativeEnum(FilteredActivityFeedTypes),
  eager: true,
});

const ActivityFeedGroupFilterPersistence = PersistenceStorage({
  key: '@soundxyz/mainActivityFeedGroupFilter',
  schema: z.array(z.nativeEnum(ActivityFeedGroupFilterOption)),
  eager: true,
});

export const setActivityFeedFilter = (type: FilteredActivityFeedTypes) => {
  if (store.activityFeedFilter.activityFeedType === type) return;
  store.activityFeedFilter = { activityFeedType: type };

  ActivityFeedMainFilterPersistence.set(type);
};

const getActivityFeedGroupFilterTypes = (snapshot: ActivityFeedStateType) => {
  return snapshot.activityFeedGroupFilter.types?.length
    ? snapshot.activityFeedGroupFilter.types
    : [ActivityFeedGroupFilterOption.All]; // Fall back to All if no types are selected
};

export function useActivityFeed({ disabled }: { disabled: boolean }) {
  const { authUser, isConnectingAPIUser } = useAuth();
  const snapshot = useActivityFeedState();
  const { login } = useConnectedAuth();
  const { activityFeedFilter, activityFeedGroupFilter } = snapshot;

  const authUserId = authUser?.id;
  const { viewingPostsEnabled } = usePostsEnabled();

  const {
    orderedList: listData,
    isLoadingNewPage: loading,
    loadMoreNextPage: loadMore,
    error,
    hasNextPage = false,
    latestData,
    refetch,
  } = useInfiniteQuery(ActivityFeedHookDocument, {
    getNextPageParam({ data }) {
      return (
        data.activityFeed?.activityFeedGroups.pageInfo.hasNextPage && {
          after: data.activityFeed.activityFeedGroups.pageInfo.endCursor,
        }
      );
    },
    filterQueryKey: {
      activityFeedFilter,
      activityFeedGroupFilter,
      authUserId,
    },
    variables({ pageParam }) {
      const after = pageParam?.after || null;

      return {
        pagination: {
          first: after == null ? PAGE_SIZE * 2 : PAGE_SIZE,
          after,
        },
        activityFeedFilter,
        activityFeedGroupFilter: {
          types: getActivityFeedGroupFilterTypes(snapshot),
        },
        userId: authUserId,
      };
    },
    enabled: !disabled && !!snapshot.fetchedFilterSettingsFromLocalStorage && !isConnectingAPIUser,
    list({ activityFeed }) {
      return addDividerDateCheck(
        filterOutRevertedActivity(
          filterOutNonExistentType(
            getManyFromList(activityFeed?.activityFeedGroups.edges, edge => {
              return !!edge?.node && edge.node;
            }),
          ),
        ),
      );
    },
    uniq({ id }) {
      return id;
    },
    // Don't refetch on window focus or reconnect to avoid breaking scroll restoration  on back button
    // Or losing scroll position when user was scrolled down
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });

  useEffect(() => {
    Promise.all([
      ActivityFeedMainFilterPersistence.get().then(localActivityFeedFilter => {
        if (!localActivityFeedFilter) return;

        if (store.activityFeedFilter.activityFeedType !== localActivityFeedFilter) {
          store.activityFeedFilter = {
            activityFeedType: localActivityFeedFilter,
          };
        }
      }),

      ActivityFeedGroupFilterPersistence.initialValue.then(localActivityFeedGroupFilter => {
        if (!localActivityFeedGroupFilter) return;

        if (
          !isEqual(
            store.activityFeedGroupFilter.types?.filter(
              v => v !== ActivityFeedGroupFilterOption.All,
            ),
            localActivityFeedGroupFilter,
          )
        ) {
          store.activityFeedGroupFilter = {
            types: localActivityFeedGroupFilter,
          };
        }
      }),
    ]).then(() => {
      store.fetchedFilterSettingsFromLocalStorage = true;
    });
  }, []);

  const activityFeedType = snapshot.activityFeedFilter.activityFeedType;

  let filteredData = listData;
  if (!viewingPostsEnabled) {
    filteredData = filteredData.filter(
      item => item.information.__typename !== 'UserCreatedPostAggregate',
    );
  } else {
    filteredData = filteredData.filter(
      item =>
        item.information.__typename !== 'ShelfCreatedAggregate' &&
        item.information.__typename !== 'ReleasesAddedToShelfAggregate',
    );
  }

  return {
    data: filteredData,
    loading,
    loadMore,
    refetch,
    error,
    hasNextPage,
    activityFeedFilter:
      activityFeedType && isActivityFeedType(activityFeedType)
        ? activityFeedType
        : FilteredActivityFeedTypes.NotableCollectors,
    setActivityFeedFilter: (selection: FilteredActivityFeedTypes) => {
      if (!authUser && selection === FilteredActivityFeedTypes.User) {
        login();
        return;
      }

      setActivityFeedFilter(selection);
    },
    activityFeedGroupFilter: snapshot.activityFeedGroupFilter.types || [
      ActivityFeedGroupFilterOption.All,
    ],
    setActivityFeedGroupFilter: (selection: ActivityFeedGroupFilterOption[]) => {
      if (isEqual(snapshot.activityFeedGroupFilter.types, selection)) return;
      const newActivityFeedGroupFilter = selection?.filter(
        option => option !== ActivityFeedGroupFilterOption.All,
      );
      store.activityFeedGroupFilter = { types: newActivityFeedGroupFilter };

      ActivityFeedGroupFilterPersistence.set(newActivityFeedGroupFilter);
    },
    id: latestData?.data.activityFeed?.id,
    fetchedFilterSettingsFromLocalStorage: snapshot.fetchedFilterSettingsFromLocalStorage,
  };
}
